import { ProposalDecision, decisionShare, proposalCount, } from '@debbie/common/dist/agents/index.js';
import { fillPlaceholders } from '@debbie/common/dist/lang/placeholders.js';
import { HOURS_PER_DAY, MINUTES_PER_HOUR, } from '@debbie/common/dist/util/dates.js';
import moment from 'moment';
import React from 'react';
import { useSelector } from 'react-redux';
import THEME from '../../theme.js';
import StatTiles from '../Insights/StatTiles.js';
import Panel from '../Panel.js';
import { percent } from './shared.js';
const PERCENTAGE_POINTS = 100;
const TONE_COLOR = {
    better: THEME.SUCCESS,
    worse: THEME.DANGEROUS,
    neutral: THEME.COLOR_GREY,
};
const oneDecimal = (value) => value.toLocaleString(undefined, { maximumFractionDigits: 1 });
const decisionTime = (hours, lang) => {
    if (hours < 1) {
        return fillPlaceholders(lang.agentPerformance.duration.minutes, {
            minutes: Math.round(hours * MINUTES_PER_HOUR),
        });
    }
    if (hours < HOURS_PER_DAY) {
        return fillPlaceholders(lang.agentPerformance.duration.hours, {
            hours: oneDecimal(hours),
        });
    }
    return fillPlaceholders(lang.agentPerformance.duration.days, {
        days: oneDecimal(hours / HOURS_PER_DAY),
    });
};
const acceptedShare = (performance) => {
    const approved = decisionShare(performance.decisions, ProposalDecision.ApprovedAsIs);
    const edited = decisionShare(performance.decisions, ProposalDecision.Edited);
    return approved != null && edited != null ? approved + edited : null;
};
const editedShare = (performance) => decisionShare(performance.decisions, ProposalDecision.Edited);
const escalatedShare = (performance) => performance.runs === 0 ? null : performance.escalatedRuns / performance.runs;
// `formatted` is the rounded size of the change, so a change that rounds to
// zero reads as no change.
const changeOf = (delta, formatted, isZero, preferred) => {
    if (isZero) {
        return { text: `± ${formatted}`, tone: 'neutral' };
    }
    const improved = preferred === 'up' ? delta > 0 : delta < 0;
    return {
        text: `${delta > 0 ? '▲' : '▼'} ${formatted}`,
        tone: preferred === 'neither' ? 'neutral' : improved ? 'better' : 'worse',
    };
};
const shareChange = (current, previous, preferred, lang) => {
    if (current == null || previous == null) {
        return null;
    }
    const points = Math.round((current - previous) * PERCENTAGE_POINTS);
    return changeOf(points, fillPlaceholders(lang.agentPerformance.percentagePoints, {
        points: Math.abs(points),
    }), points === 0, preferred);
};
const relativeChange = (current, previous, preferred) => {
    if (current == null || previous == null || previous === 0) {
        return null;
    }
    const delta = (current - previous) / previous;
    const formatted = percent(Math.abs(delta));
    return changeOf(delta, formatted, formatted === percent(0), preferred);
};
const Overview = ({ performance, previous }) => {
    const lang = useSelector((s) => s.lang);
    const { decisions, averageDecisionHours, oldestPendingAt } = performance;
    const accepted = acceptedShare(performance);
    const edited = editedShare(performance);
    const escalated = escalatedShare(performance);
    const tiles = [
        {
            label: lang.agentPerformance.accepted,
            value: accepted != null ? percent(accepted) : '–',
            change: previous && shareChange(accepted, acceptedShare(previous), 'up', lang),
        },
        {
            label: lang.agentPerformance.proposals,
            value: `${proposalCount(decisions)}`,
            change: previous &&
                relativeChange(proposalCount(decisions), proposalCount(previous.decisions), 'neither'),
        },
        {
            label: lang.agentPerformance.decisionTime,
            value: averageDecisionHours != null
                ? decisionTime(averageDecisionHours, lang)
                : '–',
            change: previous &&
                relativeChange(averageDecisionHours, previous.averageDecisionHours, 'down'),
        },
        {
            label: lang.agentPerformance.decisions[ProposalDecision.Edited],
            value: edited != null ? percent(edited) : '–',
            change: previous && shareChange(edited, editedShare(previous), 'down', lang),
        },
        {
            label: lang.agentPerformance.decisions[ProposalDecision.Pending],
            value: `${decisions[ProposalDecision.Pending]}`,
            detail: oldestPendingAt != null
                ? fillPlaceholders(lang.agentPerformance.oldestPending, {
                    age: moment(oldestPendingAt).fromNow(true),
                })
                : undefined,
        },
        {
            label: lang.agentPerformance.escalated,
            value: escalated != null ? percent(escalated) : '–',
            change: previous &&
                shareChange(escalated, escalatedShare(previous), 'neither', lang),
        },
    ];
    return (React.createElement(Panel, null,
        React.createElement(StatTiles, { tiles: tiles.map((tile) => ({
                label: tile.label,
                value: tile.value,
                footnotes: [
                    ...(tile.change != null
                        ? [
                            {
                                text: tile.change.text,
                                color: TONE_COLOR[tile.change.tone],
                                title: lang.agentPerformance.comparedToPrevious,
                            },
                        ]
                        : []),
                    ...(tile.detail != null
                        ? [{ text: tile.detail, color: THEME.COLOR_GREY }]
                        : []),
                ],
            })) })));
};
export default Overview;
//# sourceMappingURL=Overview.js.map